import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import {
  Alert,
  PageHeader,
  Container,
  Stack,
  ScrollToTopButton,
  Button,
  ProgressSpinner,
} from '@/components/ui';
import { MainLayout } from '@/layouts/MainLayout';
import { PositionList } from '@/components/positionList/PositionList';
import { usePositionListStore } from '@/store/positionListStore';
import { filterPositions } from '@/utils/positionListUtils';
import type { Position } from '@/types/positionList.types';

export function PositionListPage() {
  const navigate = useNavigate();
  const {
    areas,
    positions,
    selectedArea,
    searchQuery,
    loading,
    error,
    loadingAreas,
    refreshing,
    setSelectedArea,
    setSearchQuery,
    fetchAreas,
    fetchPositions,
    refreshPositions,
    clearError,
  } = usePositionListStore();

  // Fetch areas and positions on mount
  useEffect(() => {
    if (areas.length === 0 && !loadingAreas) {
      fetchAreas();
    }

    const areaToFetch = selectedArea || 'AG';
    fetchPositions(areaToFetch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (areas.length > 0 && !selectedArea) {
      setSelectedArea(areas[0]);
    }
  }, [areas, selectedArea, setSelectedArea]);

  const handleRefresh = async () => {
    clearError();
    if (!selectedArea) return;
    await refreshPositions();
  };

  const handleViewDetails = (position: Position) => {
    navigate(`/position-list/${selectedArea}/${position.id}`);
  };

  const filteredPositions = filterPositions(positions, searchQuery);

  const listReady = !(error && areas.length === 0);
  const title =
    positions.length > 0
      ? `Position List (${filteredPositions.length}${searchQuery ? ` of ${positions.length}` : ''})`
      : 'Position List';

  return (
    <MainLayout>
      <Container className="pb-8">
        <Stack direction="vertical" spacing={6} className="position-list-page min-w-0">
          <PageHeader
            title={title}
            actionsInline
            actions={
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing || loading || !selectedArea}
                title="Refresh"
                aria-label="Refresh"
                className="!min-h-9 !min-w-9 shrink-0 !px-2 !py-2"
              >
                {refreshing ? <ProgressSpinner size="sm" /> : <RefreshCw className="h-4 w-4" />}
              </Button>
            }
          />

          {error && areas.length === 0 && (
            <Alert
              variant="error"
              className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div>
                <p className="font-medium">Error loading areas</p>
                <p className="text-sm opacity-90">{error}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={clearError}>
                  Dismiss
                </Button>
                <Button size="sm" onClick={() => fetchAreas()}>
                  Try Again
                </Button>
              </div>
            </Alert>
          )}

          {listReady && (
            <PositionList
              positions={filteredPositions}
              loading={loading}
              error={error}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              onViewDetails={handleViewDetails}
              onRefresh={handleRefresh}
              onDismissError={clearError}
              refreshing={refreshing}
              areas={areas}
              selectedArea={selectedArea || ''}
              onAreaChange={setSelectedArea}
              loadingAreas={loadingAreas}
            />
          )}

          <ScrollToTopButton />
        </Stack>
      </Container>
    </MainLayout>
  );
}
